import React, { createContext, useContext } from 'react';
import { useQuery } from '@tanstack/react-query';
import { baseURL } from '../../../aws-config';
import { FALLBACK_PRICING } from '../data/pricing';

const PricingContext = createContext(undefined);

// Safe proxy that returns fallback pricing for any key access
// This prevents crashes when catalog.jsx accesses PRICING.xxx.price at import time
const createSafePricingProxy = () => {
    const defaultValue = { price: 0, mrp: 0 };
    return new Proxy({}, {
        get: (target, prop) => {
            return FALLBACK_PRICING[prop] || defaultValue;
        }
    });
};

export const PricingProvider = ({ children }) => {
    const fetchPricing = async () => {
        const response = await fetch(`${baseURL}api/interview-prep/pricing/`);
        if (!response.ok) {
            throw new Error('Failed to fetch pricing from backend');
        }
        return response.json();
    };

    const { data: pricingData, isLoading, error } = useQuery({
        queryKey: ['pricing'],
        queryFn: fetchPricing,
        staleTime: 5 * 60 * 1000, // Cache for 5 minutes
        retry: 2,
    });

    const pricing = pricingData || {};

    const value = {
        pricing,
        isLoading,
        error: error ? error.message : null,
        getPrice: (moduleId) => {
            const backendData = pricing[moduleId];
            const fallbackData = FALLBACK_PRICING[moduleId] || { price: 0, mrp: 0 };
            
            // Prioritize backend price if it is valid and greater than 0
            if (backendData && backendData.price > 0) {
                return backendData;
            }
            
            // Otherwise, strictly use the fallback pricing (prevents accidental 0/Free pricing)
            return fallbackData;
        },
    };

    return (
        <PricingContext.Provider value={value}>
            {children}
        </PricingContext.Provider>
    );
};

export const usePricing = () => {
    const context = useContext(PricingContext);
    if (context === undefined) {
        return {
            pricing: {},
            isLoading: true,
            error: null,
            getPrice: (moduleId) => FALLBACK_PRICING[moduleId] || { price: 0, mrp: 0 },
        };
    }
    return context;
};

// Safe PRICING export for catalog.jsx static imports
// Uses Proxy to return { price: 0, mrp: 0 } for any module access
export const PRICING = createSafePricingProxy();

export default PricingProvider;
